import React, { useState, useEffect } from 'react';
import api from '../services/api';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { 
  DocumentTextIcon, 
  DocumentDownloadIcon, 
  FilterIcon,
  XIcon,
  SearchIcon,
  CalendarIcon,
  CheckCircleIcon,
  CogIcon,
  MailIcon
} from '@heroicons/react/outline';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CustomReportBuilder from '../components/CustomReportBuilder';

export default function Reports() {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Main state
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    carMakes: [],
    carModels: [],
    carYears: [],
    nationalities: []
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    column: '',
    direction: 'asc'
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [reportType, setReportType] = useState('policies');
  const [showFilters, setShowFilters] = useState(false);
  const [customReportData, setCustomReportData] = useState(null);
  const [showCustomReportBuilder, setShowCustomReportBuilder] = useState(false);
  const [customReportConfig, setCustomReportConfig] = useState(null);
  
  // Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailOption, setEmailOption] = useState('profile'); // 'profile' or 'custom'
  const [customEmail, setCustomEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Policy type filters (for policy reports tab)
  const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({
    'Mandatory Car Insurance': true,
    'Third Party Liability': true,
    'Cargo Transport Insurance - Sea': true,
    'Cargo Transport Insurance - Air': true,
    'Cargo Transport Insurance - Road': true
  });
  
  // Insurance type filter (for claims reports tab)
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState('');
  
  const [filters, setFilters] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    nationality: '',
    carMake: '',
    carModel: '',
    carYear: '',
    claimStatus: '',
    minAmount: '',
    maxAmount: '',
    paymentStatus: '',
    paymentMethod: '',
    companyId: '',
    branchId: ''
  });

  // Columns configuration for different report types
  const getColumnConfig = () => ({
    policies: [
      { id: 'policyNumber', label: t('claims.policyNumber') },
      { id: 'clientName', label: t('claims.clientName') },
      { id: 'policyType', label: t('claims.policyType') },
      { id: 'startDate', label: t('policy.startDate') },
      { id: 'endDate', label: t('policy.endDate') },
      { id: 'limitOfIndemnity', label: t('claims.limitOfIndemnity') },
      { id: 'premium', label: t('claims.premium') },
      { id: 'totalPremium', label: t('policy.totalPremium') },
      { id: 'paymentStatus', label: t('policy.paymentStatus') },
      { id: 'paymentMethod', label: t('policy.paymentMethod') },
      { id: 'carMake', label: t('policy.vehicleMake') },
      { id: 'carModel', label: t('policy.vehicleModel') },
      { id: 'carYear', label: t('policy.vehicleYear') },
      { id: 'nationality', label: t('client.nationality') },
      { id: 'companyName', label: t('common.company') },
      { id: 'branchName', label: t('common.branch') },
      { id: 'createdAt', label: t('common.createdAt') },
      { id: 'updatedAt', label: t('common.updatedAt') }
    ],
    claims: [
      { id: 'claimNumber', label: t('claims.claimNumber') },
      { id: 'policyNumber', label: t('claims.policyNumber') },
      { id: 'clientName', label: t('claims.clientName') },
      { id: 'outstandingAmount', label: t('claims.outstandingAmount') },
      { id: 'informedDate', label: t('claims.informedDate') },
      { id: 'accidentDate', label: t('claims.accidentDate') },
      { id: 'status', label: t('common.status') },
      { id: 'settlementAmount', label: t('claims.settlementAmount') },
      { id: 'settlementDate', label: t('claims.settlementDate') },
      { id: 'carMake', label: t('policy.vehicleMake') },
      { id: 'carModel', label: t('policy.vehicleModel') },
      { id: 'carYear', label: t('policy.vehicleYear') },
      { id: 'nationality', label: t('client.nationality') },
      { id: 'companyName', label: t('common.company') },
      { id: 'branchName', label: t('common.branch') },
      { id: 'createdAt', label: t('common.createdAt') },
      { id: 'updatedAt', label: t('common.updatedAt') }
    ]
  });

  const columnConfig = getColumnConfig();

  // Fetch reports and filter options on component mount or when report type changes
  useEffect(() => {
    fetchFilterOptions();
    fetchReports();
  }, [reportType]);

  // Fetch when pagination or sorting changes
  useEffect(() => {
    if (reportType) { // Only fetch if report type is set
      fetchReports();
    }
  }, [pagination.page, sorting.column, sorting.direction]);

  // Apply client-side search filtering
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const columns = getActiveColumns();
      
      const filtered = data.filter(item => {
        return columns.some(column => {
          const value = column.getter ? column.getter(item) : item[column.id];
          return value && String(value).toLowerCase().includes(searchTermLower);
        });
      });
      
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  // Fetch filter options for dropdown menus
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/api/reports/filter-options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      toast.error(t('reports.exportFailed'));
    }
  };

  // Fetch reports based on selected filters
  const fetchReports = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('reportType', reportType);
      
      // Add pagination parameters (not used for custom reports)
      if (reportType !== 'custom') {
        params.append('page', pagination.page);
        params.append('limit', pagination.limit);
        
        // Add sorting parameters
        if (sorting.column) {
          params.append('sortColumn', sorting.column);
          params.append('sortDirection', sorting.direction);
        }
      }
      
      // Add policy type filters (for policies report)
      if (reportType === 'policies') {
        const activePolicyTypes = Object.keys(selectedPolicyTypes).filter(k => selectedPolicyTypes[k]);
        // Only add filter if not all types are selected (to avoid unnecessary filtering)
        if (activePolicyTypes.length > 0 && activePolicyTypes.length < 5) {
          params.append('policyTypes', JSON.stringify(activePolicyTypes));
        }
      }
      
      // Add insurance type filter (for claims report)
      if (reportType === 'claims' && insuranceTypeFilter) {
        params.append('insuranceType', insuranceTypeFilter);
      }
      
      // Add active filters to query
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/reports?${params.toString()}`);
      
      // Handle custom reports differently
      if (reportType === 'custom') {
        setCustomReportData(response.data);
        setData([]);
        setFilteredData([]);
      } else {
        // Set data and pagination info for regular reports
        setData(response.data.items || []);
        setFilteredData(response.data.items || []);
        setPagination({
          page: response.data.page || 1,
          limit: response.data.limit || 10,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0
        });
        setCustomReportData(null);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error(t('reports.loadingReport'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch custom report with dynamic configuration
  const fetchCustomReport = async (config) => {
    setLoading(true);
    try {
      const response = await api.post('/api/reports/custom-query', {
        columns: config.columns,
        filters: config.filters,
        sortBy: config.sortBy,
        page: pagination.page,
        limit: pagination.limit
      });

      setData(response.data.items || []);
      setFilteredData(response.data.items || []);
      setPagination({
        page: response.data.page || 1,
        limit: response.data.limit || 10,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0
      });
      setCustomReportData(null);
      
      toast.success(t('reports.reportGenerated') || 'Report generated successfully');
    } catch (error) {
      console.error('Failed to fetch custom report:', error);
      toast.error(t('reports.loadingReport'));
    } finally {
      setLoading(false);
    }
  };

  // Handle custom report builder apply
  const handleCustomReportApply = (config) => {
    setCustomReportConfig(config);
    setShowCustomReportBuilder(false);
    setReportType('custom-builder');
    fetchCustomReport(config);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search term change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    fetchReports();
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      nationality: '',
      carMake: '',
      carModel: '',
      carYear: '',
      claimStatus: '',
      minAmount: '',
      maxAmount: '',
      paymentStatus: '',
      paymentMethod: '',
      companyId: '',
      branchId: ''
    });
  };

  // Handle report type change
  const handleReportTypeChange = (e) => {
    setReportType(e.target.value);
    resetFilters();
    setSearchTerm('');
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      // Get column config for current report type
      const columns = columnConfig[reportType];
      
      // Use filtered data for export
      const dataToExport = searchTerm ? filteredData : data;
      
      // Define headers
      const headers = columns.map(col => col.label);
      
      // Convert data to CSV format
      const csvRows = [];
      
      // Add BOM for UTF-8 encoding to ensure proper Arabic text display
      const BOM = '\uFEFF';
      
      // Add headers row
      csvRows.push(headers.join(','));
      
      // Add data rows
      dataToExport.forEach(item => {
        const values = columns.map(column => {
          let value = column.getter ? column.getter(item) : item[column.id];
          
          // Handle special data types
          if (value instanceof Date) {
            value = moment(value).format('YYYY-MM-DD');
          }
          
          // Handle nulls
          if (value === null || value === undefined) {
            value = '';
          }
          
          // Escape commas and quotes
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        
        csvRows.push(values.join(','));
      });
      
      // Combine rows into a single string with BOM for UTF-8
      const csvString = BOM + csvRows.join('\n');
      
      // Create a Blob with explicit UTF-8 encoding
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${reportType}_report_${moment().format('YYYY-MM-DD')}.csv`);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error(t('reports.exportFailed'));
    }
  };

  // Export to PDF using backend (Puppeteer for proper Arabic support)
  const exportToPDF = async () => {
    try {
      setLoading(true);
      
      // Get column config for current report type
      const columns = columnConfig[reportType];
      
      // Use filtered data for export
      const dataToExport = searchTerm ? filteredData : data;
      
      // Prepare export data
      const exportData = {
        reportType,
        columns: columns.map(col => ({ id: col.id, label: col.label })),
        data: dataToExport.map(item => {
          const row = {};
          columns.forEach(column => {
            let value = column.getter ? column.getter(item) : item[column.id];
            
            // Format dates
            if (column.id.includes('Date') && value) {
              value = moment(value).format('YYYY-MM-DD');
            }
            
            // Format currency
            if (column.id.includes('Amount') || column.id.includes('Premium') || column.id.includes('Indemnity')) {
              value = `${parseFloat(value || 0).toFixed(2)} LYD`;
            }
            
            // Format payment status
            if (column.id === 'paymentStatus') {
              value = value === 'paid' ? t('policy.paid') : t('policy.unpaid');
            }
            
            // Format claim status
            if (column.id === 'status' && reportType === 'claims') {
              value = t(`claims.${value}`);
            }
            
            row[column.id] = value || '-';
          });
          return row;
        }),
        filters: {
          startDate: filters.startDate,
          endDate: filters.endDate
        },
        title: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} ${t('reports.title')}`,
        dateRangeLabel: t('reports.dateRange')
      };

      // Call backend API to generate PDF
      const response = await api.post('/api/reports/generate-pdf', exportData, {
        responseType: 'blob'
      });

      // Download the generated PDF
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report_${moment().format('YYYY-MM-DD')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error(t('reports.exportFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Email report
  const handleEmailReport = async () => {
    try {
      setSendingEmail(true);
      
      // Determine recipient email
      let recipientEmail;
      if (emailOption === 'profile') {
        recipientEmail = user?.email;
        if (!recipientEmail) {
          toast.error('No email address found in your profile');
          return;
        }
      } else {
        recipientEmail = customEmail;
        if (!recipientEmail || !recipientEmail.includes('@')) {
          toast.error('Please enter a valid email address');
          return;
        }
      }
      
      // Get column config for current report type
      const columns = reportType === 'custom-builder' && customReportConfig 
        ? customReportConfig.columns 
        : columnConfig[reportType];
      
      // Use filtered data for export
      const dataToExport = searchTerm ? filteredData : data;
      
      // Prepare export data
      const exportData = {
        reportType,
        columns: columns.map(col => ({ id: col.id, label: col.label })),
        data: dataToExport.map(item => {
          const row = {};
          columns.forEach(column => {
            let value = column.getter ? column.getter(item) : item[column.id];
            
            // Format dates
            if (column.id.includes('Date') && value) {
              value = moment(value).format('YYYY-MM-DD');
            }
            
            // Format currency
            if (column.id.includes('Amount') || column.id.includes('Premium') || column.id.includes('Indemnity')) {
              value = `${parseFloat(value || 0).toFixed(2)} LYD`;
            }
            
            // Format payment status
            if (column.id === 'paymentStatus') {
              value = value === 'paid' ? t('policy.paid') : t('policy.unpaid');
            }
            
            // Format claim status
            if (column.id === 'status' && reportType === 'claims') {
              value = t(`claims.${value}`);
            }
            
            row[column.id] = value || '-';
          });
          return row;
        }),
        filters: {
          startDate: filters.startDate,
          endDate: filters.endDate
        },
        title: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} ${t('reports.title')}`,
        dateRangeLabel: t('reports.dateRange')
      };

      // Call backend API to email the report
      await api.post('/api/reports/email-report', {
        reportData: exportData,
        recipientEmail
      });
      
      toast.success(`Report sent to ${recipientEmail}`);
      setShowEmailModal(false);
      setCustomEmail('');
      setEmailOption('profile');
    } catch (error) {
      console.error('Error emailing report:', error);
      toast.error(error.response?.data?.details || 'Failed to send report via email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Get active columns based on report type
  const getActiveColumns = () => {
    // For custom-builder reports, use the config columns
    if (reportType === 'custom-builder' && customReportConfig) {
      return customReportConfig.columns.map(colId => {
        // Find the column definition
        const allColumns = [...columnConfig.policies, ...columnConfig.claims];
        return allColumns.find(c => c.id === colId) || { id: colId, label: colId };
      });
    }
    return columnConfig[reportType] || [];
  };

  // Render cell value with proper formatting
  const renderCellValue = (column, item) => {
    const value = column.getter ? column.getter(item) : item[column.id];
    
    if (value === null || value === undefined) {
      return '-';
    }
    
    // Format dates
    if (column.id.includes('Date') && value) {
      return moment(value).format('YYYY-MM-DD');
    }
    
    // Format currency
    if (column.id.includes('Amount') || column.id.includes('Premium') || column.id.includes('Indemnity')) {
      return `${parseFloat(value).toFixed(2)} LYD`;
    }
    
    // Format payment status
    if (column.id === 'paymentStatus') {
      return value === 'paid' ? t('policy.paid') : t('policy.unpaid');
    }
    
    // Format claim status
    if (column.id === 'status' && reportType === 'claims') {
      return t(`claims.${value}`);
    }
    
    return value;
  };

  // Handle column sorting
  const handleSort = (columnId) => {
    // Toggle direction if clicking on the same column
    if (sorting.column === columnId) {
      setSorting({
        column: columnId,
        direction: sorting.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // Set new column with default ascending direction
      setSorting({
        column: columnId,
        direction: 'asc'
      });
    }
  };

  // Get sort indicator icon
  const getSortIcon = (columnId) => {
    if (sorting.column !== columnId) {
      return null;
    }
    
    return (
      <span className="ml-1">
        {sorting.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Handle policy type checkbox change
  const handlePolicyTypeChange = (policyType) => {
    setSelectedPolicyTypes(prev => ({
      ...prev,
      [policyType]: !prev[policyType]
    }));
  };

  // Handle select all policy types
  const handleSelectAllPolicyTypes = () => {
    const allSelected = Object.values(selectedPolicyTypes).every(v => v);
    const newState = {};
    Object.keys(selectedPolicyTypes).forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedPolicyTypes(newState);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) {
      return;
    }
    
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">{t('reports.title')}</h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('reports.description')}
          </p>
        </div>
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex px-6" aria-label="Tabs">
            <button
              onClick={() => setReportType('policies')}
              className={`${
                reportType === 'policies'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`}
            >
              📄 {t('reports.policyReports')}
            </button>
            <button
              onClick={() => setReportType('claims')}
              className={`${
                reportType === 'claims'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`}
            >
              📋 {t('reports.claimsReports')}
            </button>
            <button
              onClick={() => setReportType('custom-builder')}
              className={`${
                reportType === 'custom-builder'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`}
            >
              ⚙️ {t('reports.customReportBuilder')}
            </button>
          </nav>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {/* Policy Type Checkboxes (shown when reportType === 'policies') */}
          {reportType === 'policies' && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t('reports.selectPolicyTypes')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Object.values(selectedPolicyTypes).every(v => v)}
                    onChange={handleSelectAllPolicyTypes}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">{t('reports.selectAll')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPolicyTypes['Mandatory Car Insurance']}
                    onChange={() => handlePolicyTypeChange('Mandatory Car Insurance')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{t('reports.mandatoryCar')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPolicyTypes['Third Party Liability']}
                    onChange={() => handlePolicyTypeChange('Third Party Liability')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{t('reports.thirdPartyLiability')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPolicyTypes['Cargo Transport Insurance - Sea']}
                    onChange={() => handlePolicyTypeChange('Cargo Transport Insurance - Sea')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{t('reports.cargoSea')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPolicyTypes['Cargo Transport Insurance - Air']}
                    onChange={() => handlePolicyTypeChange('Cargo Transport Insurance - Air')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{t('reports.cargoAir')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPolicyTypes['Cargo Transport Insurance - Road']}
                    onChange={() => handlePolicyTypeChange('Cargo Transport Insurance - Road')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{t('reports.cargoRoad')}</span>
                </label>
              </div>
            </div>
          )}

          {/* Insurance Type Filter (shown when reportType === 'claims') */}
          {reportType === 'claims' && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <label htmlFor="insuranceTypeFilter" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('reports.insuranceTypeFilter')}
              </label>
              <select
                id="insuranceTypeFilter"
                value={insuranceTypeFilter}
                onChange={(e) => setInsuranceTypeFilter(e.target.value)}
                className="block w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">{t('reports.allInsuranceTypes')}</option>
                <option value="Mandatory Car Insurance">{t('reports.mandatoryCar')}</option>
                <option value="Third Party Liability">{t('reports.thirdPartyLiability')}</option>
                <option value="Cargo Transport Insurance - Sea">{t('reports.cargoSea')}</option>
                <option value="Cargo Transport Insurance - Air">{t('reports.cargoAir')}</option>
                <option value="Cargo Transport Insurance - Road">{t('reports.cargoRoad')}</option>
              </select>
            </div>
          )}

          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="sm:flex sm:items-center space-x-4">

              <div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="mt-5 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FilterIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                  {t('reports.filters')}
                </button>
              </div>

              <div className="mt-5 relative flex-grow max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="search"
                  id="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  placeholder={t('reports.searchPlaceholder')}
                />
              </div>
            </div>

            <div className="mt-5 flex space-x-2">
              <button
                type="button"
                onClick={() => setShowCustomReportBuilder(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <CogIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                {t('reports.buildCustomReport') || 'Build Custom Report'}
              </button>
              <button
                type="button"
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <DocumentDownloadIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                {t('reports.exportToCsv')}
              </button>
              <button
                type="button"
                onClick={exportToPDF}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <DocumentTextIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('reports.exportToPdf')}
              </button>
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <MailIcon className="-ml-1 mr-2 h-5 w-5" />
                📧 Email Report
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 bg-gray-50 p-4 sm:p-6 rounded-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('reports.filters')}</h3>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={applyFilters}>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                      {t('reports.startDate')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        name="startDate"
                        id="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                      {t('reports.endDate')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        name="endDate"
                        id="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Company and Branch Selection - based on user role */}
                  {(user?.role === 'admin' || user?.role === 'governing_body' || user?.role === 'company') && (
                    <>
                      <div className="sm:col-span-3">
                        <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                          {t('common.company')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="companyId"
                            name="companyId"
                            value={filters.companyId}
                            onChange={(e) => {
                              handleFilterChange(e);
                              // Reset branch when company changes
                              if (e.target.value !== filters.companyId) {
                                setFilters(prev => ({ ...prev, branchId: '' }));
                              }
                            }}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">{t('common.all')}</option>
                            {filterOptions.companies?.map(company => (
                              <option key={company.id} value={company.id}>
                                {company.name} ({company.abbreviation})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="branchId" className="block text-sm font-medium text-gray-700">
                          {t('common.branch')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="branchId"
                            name="branchId"
                            value={filters.branchId}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            disabled={!filters.companyId}
                          >
                            <option value="">{t('common.all')}</option>
                            {filterOptions.branches
                              ?.filter(branch => !filters.companyId || branch.companyId == filters.companyId)
                              .map(branch => (
                                <option key={branch.id} value={branch.id}>
                                  {branch.name} ({branch.company?.name})
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {(reportType === 'policies' || reportType === 'claims') && (
                    <>
                      <div className="sm:col-span-2">
                        <label htmlFor="carMake" className="block text-sm font-medium text-gray-700">
                          {t('policy.vehicleMake')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="carMake"
                            name="carMake"
                            value={filters.carMake}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">{t('common.all')}</option>
                            {filterOptions.carMakes.map(make => (
                              <option key={make} value={make}>{make}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="carModel" className="block text-sm font-medium text-gray-700">
                          {t('policy.vehicleModel')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="carModel"
                            name="carModel"
                            value={filters.carModel}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            disabled={!filters.carMake}
                          >
                            <option value="">{t('common.all')}</option>
                            {filterOptions.carModels
                              .filter(model => !filters.carMake || model.make === filters.carMake)
                              .map(model => (
                                <option key={model.model} value={model.model}>{model.model}</option>
                              ))}
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="carYear" className="block text-sm font-medium text-gray-700">
                          {t('policy.vehicleYear')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="carYear"
                            name="carYear"
                            value={filters.carYear}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">{t('common.all')}</option>
                            {filterOptions.carYears.map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {reportType === 'claims' && (
                    <>
                      <div className="sm:col-span-3">
                        <label htmlFor="claimStatus" className="block text-sm font-medium text-gray-700">
                          {t('common.status')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="claimStatus"
                            name="claimStatus"
                            value={filters.claimStatus}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">{t('claims.allStatuses')}</option>
                            <option value="pending">{t('claims.pending')}</option>
                            <option value="approved">{t('claims.approved')}</option>
                            <option value="rejected">{t('claims.rejected')}</option>
                            <option value="settled">{t('claims.settled')}</option>
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="minAmount" className="block text-sm font-medium text-gray-700">
                          {t('claims.minAmount')}
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            name="minAmount"
                            id="minAmount"
                            value={filters.minAmount}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {reportType === 'policies' && (
                    <>
                      <div className="sm:col-span-3">
                        <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">
                          {t('policy.paymentStatus')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="paymentStatus"
                            name="paymentStatus"
                            value={filters.paymentStatus}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">{t('common.all')}</option>
                            <option value="paid">{t('policy.paid')}</option>
                            <option value="unpaid">{t('policy.unpaid')}</option>
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                          {t('policy.paymentMethod')}
                        </label>
                        <div className="mt-1">
                          <select
                            id="paymentMethod"
                            name="paymentMethod"
                            value={filters.paymentMethod}
                            onChange={handleFilterChange}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            disabled={filters.paymentStatus !== 'paid'}
                          >
                            <option value="">{t('common.all')}</option>
                            <option value="cash">{t('policy.cash')}</option>
                            <option value="cheque">{t('policy.cheque')}</option>
                            <option value="onAccount">{t('policy.onAccount')}</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="sm:col-span-6">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {t('reports.resetFilters')}
                      </button>
                      <button
                        type="submit"
                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {t('reports.applyFilters')}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Results Table */}
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900">{t('reports.results')}</h3>
            
            {loading ? (
              <div className="mt-4 flex justify-center items-center h-32">
                <p className="text-gray-500">{t('reports.loadingReport')}</p>
              </div>
            ) : reportType === 'custom' && customReportData ? (
              <div className="mt-4 space-y-6">
                {/* Summary Statistics */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('reports.totalPolicies')}
                      </dt>
                      <dd className="mt-1 text-3xl font-semibold text-gray-900">
                        {customReportData.summary.totalPolicies}
                      </dd>
                    </div>
                  </div>
                  
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('reports.totalPremium')}
                      </dt>
                      <dd className="mt-1 text-3xl font-semibold text-gray-900">
                        {customReportData.summary.totalPremium.toFixed(2)} LYD
                      </dd>
                    </div>
                  </div>
                  
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('reports.totalClaims')}
                      </dt>
                      <dd className="mt-1 text-3xl font-semibold text-gray-900">
                        {customReportData.summary.totalClaims}
                      </dd>
                    </div>
                  </div>
                  
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('reports.totalClaimAmount')}
                      </dt>
                      <dd className="mt-1 text-3xl font-semibold text-gray-900">
                        {customReportData.summary.totalClaimAmount.toFixed(2)} LYD
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Policy Status Breakdown */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('reports.policyStatusBreakdown')}</h4>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="border-l-4 border-green-500 pl-4">
                      <p className="text-sm text-gray-500">{t('policy.paid')}</p>
                      <p className="text-2xl font-semibold text-gray-900">{customReportData.summary.paidPolicies}</p>
                      <p className="text-sm text-gray-600">{customReportData.summary.paidPremium.toFixed(2)} LYD</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                      <p className="text-sm text-gray-500">{t('policy.unpaid')}</p>
                      <p className="text-2xl font-semibold text-gray-900">{customReportData.summary.unpaidPolicies}</p>
                      <p className="text-sm text-gray-600">{customReportData.summary.unpaidPremium.toFixed(2)} LYD</p>
                    </div>
                  </div>
                </div>

                {/* Policy Type Breakdown */}
                {customReportData.breakdowns.policyTypes.length > 0 && (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">{t('reports.policyTypeBreakdown')}</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('claims.policyType')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.count')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.totalPremium')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customReportData.breakdowns.policyTypes.map((type, idx) => (
                            <tr key={idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.type}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.totalPremium.toFixed(2)} LYD</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Vehicle Makes */}
                {customReportData.breakdowns.topVehicleMakes.length > 0 && (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">{t('reports.topVehicleMakes')}</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('policy.vehicleMake')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.count')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customReportData.breakdowns.topVehicleMakes.map((make, idx) => (
                            <tr key={idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{make.make}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{make.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : filteredData.length === 0 ? (
              <div className="mt-4 bg-white border rounded-md p-6 text-center text-gray-500">
                {t('reports.noResults')}
              </div>
            ) : (
              <>
                <div className="mt-4 flex flex-col">
                  <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                          <tr>
                            {getActiveColumns().map((column) => (
                              <th
                                key={column.id}
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort(column.id)}
                              >
                                {column.label}
                                {getSortIcon(column.id)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredData.map((item, itemIdx) => (
                            <tr key={item.id || itemIdx}>
                              {getActiveColumns().map((column) => (
                                <td key={`${item.id}-${column.id}`} className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                  {renderCellValue(column, item)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between bg-white px-4 py-3 sm:px-6 mt-4 border-t border-gray-200">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                          pagination.page === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t('claims.previous')}
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                          pagination.page === pagination.totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t('claims.next')}
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          {t('claims.page')} <span className="font-medium">{pagination.page}</span> {t('claims.of')}{' '}
                          <span className="font-medium">{pagination.totalPages}</span>
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                              pagination.page === 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">{t('claims.previous')}</span>
                            <svg
                              className="h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                              pagination.page === pagination.totalPages
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">{t('claims.next')}</span>
                            <svg
                              className="h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Email Report Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MailIcon className="w-6 h-6 text-green-600" />
                Email Report
              </h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Email Option */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send to:
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="emailOption"
                      value="profile"
                      checked={emailOption === 'profile'}
                      onChange={(e) => setEmailOption(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium">My Profile Email</div>
                      <div className="text-sm text-gray-500">{user?.email || 'No email in profile'}</div>
                    </div>
                  </label>

                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="emailOption"
                      value="custom"
                      checked={emailOption === 'custom'}
                      onChange={(e) => setEmailOption(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium">Custom Email Address</div>
                      <div className="text-sm text-gray-500">Specify a different email</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Custom Email Input */}
              {emailOption === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailReport}
                  disabled={sendingEmail || (emailOption === 'custom' && !customEmail)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium flex items-center justify-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <MailIcon className="w-5 h-5" />
                      Send Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Report Builder Modal */}
      {showCustomReportBuilder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative max-w-6xl w-full mx-auto">
            <CustomReportBuilder
              onApply={handleCustomReportApply}
              onCancel={() => setShowCustomReportBuilder(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}